import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';
import { DashboardViewService } from 'src/app/services/dashboard-view.service';
import { DashboardUserViewService } from 'src/app/services/dashboard-user-view.service';
import { ToastService } from 'src/app/services/toast.service';
import { TranslateService } from 'src/app/services/translate.service';
import { AuthService } from 'src/app/services/auth.service';
import { ListFilterToolbarComponent } from 'src/app/shared/components/list-filter-toolbar/list-filter-toolbar.component';
import { DeleteDialog } from 'src/app/shared/delete-dialog/delete-dialog.component';
import { DashboardViewLookup } from 'src/app/models/dashboard-view.model';
import { SharedModule } from 'src/app/shared/shared.module';
import { ViewSelectorBase } from '../../base/view-selector-base';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import { GridPermissions } from 'src/app/shared/grid/core-grid/gridPermissions.enum';

@Component({
    standalone: true,
    selector: 'app-dashboard-view-selector',
    imports: [FormsModule, TranslateModule, SharedModule, ListFilterToolbarComponent, MatMenuModule],
    templateUrl: '../../components/view-selector/view-selector.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['../../components/view-selector/view-selector.scss']
})
export class DashboardViewSelectorComponent extends ViewSelectorBase<DashboardViewLookup> implements OnInit, OnDestroy, OnChanges {
  @Input() override defaultViewId?: string;
  @Input() override selectedViewId?: string;
  @Input() organizationLevelId?: string;
  @Input() override currentViewName?: string;

  @Output() override addView = new EventEmitter<null>();
  @Output() override viewSelected = new EventEmitter<{ view: DashboardViewLookup; firstRender: boolean }>();
  @Output() override viewsChanged = new EventEmitter<DashboardViewLookup[]>();
  @Output() editView = new EventEmitter<DashboardViewLookup>();
  @Output() defaultViewChanged = new EventEmitter<string>();
  @Output() viewDuplicated = new EventEmitter<{ originalView: DashboardViewLookup; newName: string }>();

  override views: DashboardViewLookup[] = [];
  override selectedView?: DashboardViewLookup | null = null;

  readonly permissionObject = RolePermissionObjects.DashboardView;
  readonly GridPermissions = GridPermissions;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private dashboardViewService: DashboardViewService,
    private dashboardUserViewService: DashboardUserViewService,
    private dialog: MatDialog,
    private toastService: ToastService,
    private translateService: TranslateService,
    private authService: AuthService
  ) {
    super();
  }

  protected get currentUserId(): string {
    return this.authService.getCurrentUser()?.userId ?? '';
  }

  ngOnInit(): void {
    this.fetchViews();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['defaultViewId'] && changes['defaultViewId'].currentValue !== undefined) this.sortViews();
    if (changes['selectedViewId']?.previousValue !== undefined) this.syncSelectedView();
    if (changes['organizationLevelId']?.previousValue !== undefined) this.fetchViews();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  fetchViews(): void {
    this.isLoading = true;
    const filterBy = { dashboardId: this.dashboardViewService.DASHBOARD_ID };
    this.dashboardViewService.getAll(JSON.stringify(filterBy)).pipe(
      takeUntil(this.destroy$),
      finalize(() => this.isLoading = false)
    ).subscribe({
      next: (views) => {
        this.views = views;
        this.sortViews();
        this.viewsChanged.emit(this.views);
      },
      error: () => { this.views = []; this.filteredViews = []; }
    });
  }

  async confirmRename(view: DashboardViewLookup): Promise<void> {
    if (view.canManage === false) { this.cancelRenaming(); return; }
    const newName = this.renameInput.trim();
    if (!newName || newName === view.name) {
      this.cancelRenaming();
      return;
    }
    this.isLoading = true;
    try {
      await firstValueFrom(this.dashboardViewService.rename(view.id, newName));
      view.name = newName;
      this.applyViewFilter();
      if (this.selectedView?.id === view.id) this.viewSelected.emit({ view, firstRender: false });
      this.toastService.showSuccess(this.translateService.getDynamicTranslation('View renamed successfully'), true);
    } catch { this.toastService.showError(this.translateService.getDynamicTranslation('Failed to rename view')); }
    finally { this.cancelRenaming(); this.isLoading = false; }
  }

  async makeDefaultView(view: DashboardViewLookup): Promise<void> {
    this.closeMenu();
    if (view.id === this.defaultViewId) return;
    this.isLoading = true;
    try {
      await firstValueFrom(this.dashboardUserViewService.saveDefaultView(view.id));
      this.defaultViewId = view.id;
      this.defaultViewChanged.emit(view.id);
      this.sortViews();
      this.toastService.showSuccess(this.translateService.getDynamicTranslation('Default view set successfully'), true);
    } catch { this.toastService.showError(this.translateService.getDynamicTranslation('Failed to set default view')); }
    finally { this.isLoading = false; }
  }

  async duplicateView(view: DashboardViewLookup): Promise<void> {
    this.closeMenu();
    this.isLoading = true;
    try {
      const existingNames = new Set(this.views.map(v => v.name));
      let newName = this.translateService.getDynamicTranslation(
        '{{viewName}} - Copy',
        { viewName: view.name }
      );
      let i = 1;
      while (existingNames.has(newName)) {
        newName = this.translateService.getDynamicTranslation(
          '{{viewName}} - Copy ({{index}})',
          { viewName: view.name, index: ++i }
        );
      }
      this.viewDuplicated.emit({ originalView: view, newName });
      this.isOpen = false;
    } catch { this.toastService.showError(this.translateService.getDynamicTranslation('Failed to duplicate view')); }
    finally { this.isLoading = false; }
  }

  deleteView(view: DashboardViewLookup): void {
    if (view.canManage === false) return;
    this.closeMenu();
    if (view.id === this.defaultViewId) {
      this.toastService.showError(this.translateService.getDynamicTranslation('Cannot delete the default view'));
      return;
    }
    const dialogRef = this.dialog.open(DeleteDialog, {
      data: { title: 'Dashboard View', service: null, customDelete: () => this.dashboardViewService.delete(view.id) }
    });
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.views = this.views.filter(v => v.id !== view.id);
        this.applyViewFilter();
        if (this.selectedView?.id === view.id) {
          const fallback = this.views.find(v => v.id === this.defaultViewId) || this.views[0];
          if (fallback) this.selectView(fallback, true);
        }
        this.viewsChanged.emit(this.views);
      }
    });
  }
}